import { AnalyticsBrowser, type InitOptions } from '@segment/analytics-next'

type SegmentIntegrations = NonNullable<InitOptions['integrations']>

// Delivery: standard (per-event) with `keepalive: true`. `keepalive` lets an
// in-flight request finish even as the page unloads on a navigating click — the
// direct fix for the loss we measured on "Purchase Click", where the click IS the
// navigation (to /checkout etc.) and classic analytics.js v1's fire-and-forget
// XHR was cancelled before it reached Segment. It carries a 64KB cap shared
// across all keepalive requests on the page, which is fine for our small,
// low-volume event payloads (see analytics-next `shared-dispatcher`).
//
// The built-in localStorage retry queue (`PersistedPriorityQueue`, on unless
// `disableClientPersistence`) plus its `pagehide` handler stay on as a backstop,
// replaying anything still unsent on the next same-origin page load.
const KEEPALIVE_DELIVERY_STRATEGY = {
  strategy: 'standard',
  config: { keepalive: true },
} as const

// Guard so competing entry paths (auto-load, GDPR, CCPA airgap) load Segment at
// most once — the first to run wins, matching the classic snippet's dedup.
let segmentLoaded = false

// The global `window.analytics` every tracking consumer already reads. We keep
// the classic `@types/segment-analytics` typing at the call sites and only widen
// the type here, at the single assignment point.
const analyticsWindow = () =>
  window as unknown as { analytics?: AnalyticsBrowser }

/**
 * Returns the shared buffered analytics-next instance, creating it on first use.
 * `AnalyticsBrowser` buffers `track`/`page`/`identify` calls made before
 * `.load()` and replays them once loaded, preserving the classic snippet's
 * pre-load buffering behavior.
 */
export const getAnalytics = (): AnalyticsBrowser => {
  const w = analyticsWindow()
  if (!w.analytics) {
    w.analytics = new AnalyticsBrowser()
  }
  return w.analytics
}

/**
 * Loads Segment (analytics-next) with standard delivery + `keepalive`, so an
 * in-flight request survives a navigating click (see the delivery note above).
 * The persisted retry queue remains as a backstop. No-op after the first
 * successful call and on the server.
 */
export const loadAnalytics = (
  writeKey: string,
  integrations: SegmentIntegrations = {},
): void => {
  if (typeof window === 'undefined' || segmentLoaded) return
  segmentLoaded = true

  const existingSegmentio = integrations['Segment.io']
  getAnalytics().load(
    { writeKey },
    {
      integrations: {
        ...integrations,
        'Segment.io': {
          ...(typeof existingSegmentio === 'object' ? existingSegmentio : {}),
          deliveryStrategy: KEEPALIVE_DELIVERY_STRATEGY,
        },
      },
    },
  )
}

// NOTE: we deliberately do NOT create the instance at import time here.
// `@mc/track-event` is imported by nearly every app/lib (including in Node/test
// contexts), so a top-level `new AnalyticsBrowser()` would run everywhere and,
// in jsdom, eagerly touch browser-only APIs (indexedDB) before they're polyfilled.
// The eager pre-consent buffer is created on the app-boot path instead — see the
// `getAnalytics()` call in `@mc/consent-manager-loader`.
